import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Star, Users, Home, RefreshCw, AlertCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface PriceEntry {
  id: string;
  title: string;
  content: string;
  priority: number;
}

// Helper to parse price entries from bot_knowledge
const parsePrice = (entry: PriceEntry): { name: string; price: string; unit: string; description: string; features: string[] } => {
  const content = entry.content;
  
  // Extract price (matches patterns like "99 €", "69,00 €", "ab 149 €")
  const priceMatch = content.match(/(ab\s+)?(\d+(?:,\d+)?)\s*€/i);
  const price = priceMatch ? `${priceMatch[1] || ""}${priceMatch[2]} €` : "";
  
  // Extract unit from title or content
  let unit = "";
  if (content.includes("pro Monat") || entry.title.includes("/ Monat")) unit = "/ Monat";
  else if (content.includes("pro Stunde") || entry.title.includes("/ Std")) unit = "/ Std.";
  else if (content.includes("einmalig")) unit = "(einmalig)";
  else if (content.includes("Minuten")) {
    const minMatch = content.match(/(\d+)\s*Minuten/);
    if (minMatch) unit = `(${minMatch[1]} Min.)`;
  }
  
  // Extract features (items after "Enthält:")
  const features: string[] = [];
  const featuresMatch = content.match(/Enthält:\s*(.+)/i);
  if (featuresMatch) {
    const featureList = featuresMatch[1].split(/,\s*(?=\d|[A-Z])/);
    features.push(...featureList.map(f => f.trim()).filter(f => f.length > 0));
  }
  
  return {
    name: entry.title.replace(/\s*\(Kleinunternehmen\)|\s*\(Senioren\/Privat\)|\s*-\s*EMPFOHLEN/gi, "").trim(),
    price: price.trim(),
    unit,
    description: content.split(".")[0] + ".",
    features
  };
};

const PriceOverview = () => {
  const [priceEntries, setPriceEntries] = useState<PriceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const loadPrices = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from("bot_knowledge")
        .select("id, title, content, priority")
        .eq("category", "preise")
        .eq("is_active", true)
        .order("priority", { ascending: false });

      if (fetchError) throw fetchError;
      setPriceEntries(data || []);
    } catch (err) {
      console.error("Error loading prices:", err);
      setError("Preise konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPrices();
  }, []);

  // Group entries by type
  const businessRetainers = priceEntries
    .filter(e => e.title.includes("Retainer") && e.title.includes("Kleinunternehmen"))
    .map(e => ({
      ...parsePrice(e),
      recommended: e.title.includes("EMPFOHLEN") || e.title.includes("Standard")
    }));

  const businessOneTime = priceEntries
    .filter(e => 
      e.title.includes("Kleinunternehmen") && 
      !e.title.includes("Retainer") ||
      e.title.includes("Website-Wartung") ||
      e.title.includes("IT-Check")
    )
    .map(e => {
      const parsed = parsePrice(e);
      return { service: parsed.name, price: parsed.price, unit: parsed.unit };
    });

  const seniorPackages = priceEntries
    .filter(e => e.title.includes("Sorglos Zuhause"))
    .map(e => {
      const parsed = parsePrice(e);
      return {
        name: parsed.name,
        price: parsed.price,
        unit: parsed.unit,
        features: parsed.features.length > 0 ? parsed.features : [parsed.description]
      };
    });

  const seniorPrices = priceEntries
    .filter(e => 
      e.title.includes("Senioren/Privat") && 
      !e.title.includes("Sorglos") ||
      e.title.includes("Schulungen")
    )
    .map(e => {
      const parsed = parsePrice(e);
      return { service: parsed.name, price: parsed.price, unit: parsed.unit };
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Lade Preise...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
          <Button onClick={loadPrices} variant="outline" className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Erneut versuchen
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (priceEntries.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">
            Keine Preise gefunden. Bitte Preise in der Bot-Wissensdatenbank (Kategorie "preise") hinzufügen.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <p className="text-sm text-blue-700">
            <strong>Hinweis:</strong> Diese Preise werden aus der Bot-Wissensdatenbank geladen (Kategorie "preise"). 
            Änderungen dort werden automatisch hier und im KI-Bot übernommen.
          </p>
        </CardContent>
      </Card>

      {/* Kleinunternehmen & Freiberufler */}
      {(businessRetainers.length > 0 || businessOneTime.length > 0) && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Kleinunternehmen & Freiberufler – IT-Betreuung im Monatsabo</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Statt einzelne Einsätze zu bezahlen, sichern sich Kunden mit unseren Retainer-Paketen eine laufende IT-Betreuung.
          </p>

          {/* Retainer-Pakete */}
          {businessRetainers.length > 0 && (
            <div className="grid gap-4 md:grid-cols-3">
              {businessRetainers.map((pkg, index) => (
                <Card key={index} className={pkg.recommended ? "border-primary border-2 relative" : "border"}>
                  {pkg.recommended && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                      <Star className="h-3 w-3 mr-1" /> Empfohlen
                    </Badge>
                  )}
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{pkg.name}</CardTitle>
                    <CardDescription>{pkg.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-primary mb-4">
                      {pkg.price} <span className="text-sm font-normal text-muted-foreground">{pkg.unit}</span>
                    </div>
                    {pkg.features.length > 0 && (
                      <ul className="space-y-2 text-sm">
                        {pkg.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-green-600">✓</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Einmal-Support */}
          {businessOneTime.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Einmal-Support ohne Vertrag</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Leistung</TableHead>
                      <TableHead className="text-right">Preis</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {businessOneTime.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{item.service}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {item.price} {item.unit}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Senioren & Privatpersonen */}
      {(seniorPackages.length > 0 || seniorPrices.length > 0) && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Home className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Senioren & Privatpersonen – Sorglos-Pakete</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Für alle, die regelmäßig Unterstützung brauchen, aber nicht jedes Mal neu überlegen wollen, wen sie anrufen.
          </p>

          {/* Sorglos-Pakete */}
          {seniorPackages.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2">
              {seniorPackages.map((pkg, index) => (
                <Card key={index} className="border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{pkg.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-primary mb-4">
                      {pkg.price} <span className="text-sm font-normal text-muted-foreground">{pkg.unit}</span>
                    </div>
                    <ul className="space-y-2 text-sm">
                      {pkg.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-green-600">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Einzelpreise */}
          {seniorPrices.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Flexible Hilfe (Einzelpreise)</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Leistung</TableHead>
                      <TableHead className="text-right">Preis</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {seniorPrices.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{item.service}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {item.price} {item.unit}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default PriceOverview;